import React, { useState } from "react";
import {
  Checkbox,
  FormControl,
  FormLabel,
  ListItemText,
  MenuItem,
  Select,
  Typography,
  type SelectChangeEvent,
} from "@mui/material";
import uniqueId from "lodash/uniqueId";

import { FIELD_LABEL_SX } from "./MarkerFields";
import type { MarkerGoalOption } from "./MarkerDialog.types";

/**
 * Picked targets show their checkbox only, as in Figma — no selected-row fill —
 * and sit one checkbox (24px) further in than their goal (16px row padding).
 */
const TARGET_ROW_SX = {
  pl: 10,
  "&.Mui-selected": { bgcolor: "transparent" },
  "&.Mui-selected:hover, &.Mui-selected.Mui-focusVisible": { bgcolor: "action.hover" },
} as const;

/** Menu value of a goal row. Goal rows toggle their targets instead of being selected. */
const goalRowValue = (goalId: number) => `goal-${goalId}`;

interface GoalTargetsSelectProps {
  /** Goals to pick targets from, each with its targets. */
  goals: MarkerGoalOption[];
  /** Ids of the picked targets. */
  value: number[];
  /** Called with the new list of picked target ids. */
  onChange: (targetIds: number[]) => void;
  /** Locks the select. */
  disabled?: boolean;
}

/**
 * Multi-select of targets grouped by goal. Each goal row selects or clears all
 * of its targets, and shows as indeterminate when only some are picked. The
 * closed select lists the picked targets' titles.
 *
 * @param props
 * @param props.goals    - Goals and their targets
 * @param props.value    - Picked target ids
 * @param props.onChange - Called with the new picked target ids
 * @param [props.disabled] - Locks the select
 */
const GoalTargetsSelect: React.FC<GoalTargetsSelectProps> = ({
  goals,
  value,
  onChange,
  disabled = false,
}) => {
  // Created on the first render so the select is named from the start.
  const [labelId] = useState(() => uniqueId("goal-targets-label-"));

  const titleById = new Map(goals.flatMap((goal) => goal.targets.map((t) => [t.id, t.title])));
  const picked = new Set(value);

  const handleChange = (event: SelectChangeEvent<Array<number | string>>) => {
    const next = event.target.value as Array<number | string>;
    // MUI appends the clicked item's value; a goal row toggles all its targets.
    const clickedGoal = goals.find((goal) => next.includes(goalRowValue(goal.id)));
    if (!clickedGoal) {
      onChange(next.filter((id): id is number => typeof id === "number"));
      return;
    }

    const goalTargetIds = clickedGoal.targets.map((target) => target.id);
    const allPicked = goalTargetIds.every((id) => picked.has(id));
    onChange(
      allPicked
        ? value.filter((id) => !goalTargetIds.includes(id))
        : Array.from(new Set([...value, ...goalTargetIds]))
    );
  };

  return (
    <FormControl fullWidth disabled={disabled}>
      <FormLabel id={labelId} sx={FIELD_LABEL_SX}>
        Select clinical goals
      </FormLabel>
      <Select<Array<number | string>>
        multiple
        displayEmpty
        labelId={labelId}
        value={value}
        onChange={handleChange}
        size="small"
        renderValue={() => {
          // A target picked earlier can be gone from a refreshed list (archived
          // or deleted); showing "undefined" for it would be worse than leaving
          // it out. `resolveMarkerTargetIds` drops those ids too.
          const titles = value.flatMap((id) => titleById.get(id) ?? []);
          return titles.length > 0 ? (
            titles.join(", ")
          ) : (
            <Typography variant="body.small" color="text.disabled">
              Select clinical goals
            </Typography>
          );
        }}
        sx={{ borderRadius: "10px" }}
      >
        {goals.flatMap((goal) => {
          const goalTargetIds = goal.targets.map((target) => target.id);
          const pickedCount = goalTargetIds.filter((id) => picked.has(id)).length;

          return [
            <MenuItem
              key={goalRowValue(goal.id)}
              value={goalRowValue(goal.id)}
              disabled={goalTargetIds.length === 0}
              aria-label={goal.title}
            >
              <Checkbox
                size="small"
                checked={goalTargetIds.length > 0 && pickedCount === goalTargetIds.length}
                indeterminate={pickedCount > 0 && pickedCount < goalTargetIds.length}
                sx={{ p: 0, mr: 2 }}
              />
              <ListItemText primary={goal.title} />
            </MenuItem>,
            ...goal.targets.map((target) => (
              <MenuItem
                key={target.id}
                value={target.id}
                aria-label={target.title}
                sx={TARGET_ROW_SX}
              >
                <Checkbox size="small" checked={picked.has(target.id)} sx={{ p: 0, mr: 2 }} />
                <ListItemText primary={target.title} />
              </MenuItem>
            )),
          ];
        })}
      </Select>
    </FormControl>
  );
};

export default GoalTargetsSelect;
